import { useMutation, useQuery, useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { queryClient } from "../../lib/queryClient";
import {
  bulkResetFailedCredentials,
  fetchMargaritavilleRolesByStore,
  fetchMyManagedStores,
  fetchProvisionJob,
  fetchProvisionJobLogs,
  fetchProvisionJobs,
  uploadProvisionCsv
} from "./api";

export { bulkResetFailedCredentials };
import {
  buildManagedStoresFlatListCsv,
  buildManagedStoresGroupedSummaryCsv,
  groupManagedStoresByCatalogSlice,
  triggerCsvDownload,
  type CatalogSliceGroup
} from "./managedStoresReference";
import type { ManagedStoreRow, ProvisionJobLog, ProvisionJobSummary, RolesByCatalogStore, UploadProvisionCsvPayload } from "./types";

const JOBS_QUERY_KEY = (scope?: "all" | "mine") =>
  scope ? (["provision", "jobs", scope] as const) : (["provision", "jobs"] as const);
const JOB_QUERY_KEY = (jobId: string) => ["provision", "jobs", jobId] as const;
const LOGS_QUERY_KEY = (jobId: string) => ["provision", "jobs", jobId, "logs"] as const;
export const MANAGED_STORES_QUERY_KEY = ["users", "me", "managed-stores"] as const;

export const rolesByStoreQueryKey = (storeId: string) =>
  ["catalog", "margaritaville", "roles-by-store", storeId] as const;

export const useProvisionJobs = (scope?: "all" | "mine") =>
  useQuery<ProvisionJobSummary[]>({
    queryKey: JOBS_QUERY_KEY(scope),
    queryFn: () => fetchProvisionJobs(scope),
    refetchInterval: 15000
  });

export const useProvisionJob = (jobId: string | null) =>
  useQuery<ProvisionJobSummary>({
    queryKey: JOB_QUERY_KEY(jobId ?? "__idle__"),
    queryFn: () => fetchProvisionJob(jobId ?? ""),
    enabled: Boolean(jobId),
    staleTime: 10_000
  });

type ProvisionJobLogsOptions = {
  /** Defaults to 5000 ms when omitted (job logs page). Use `false` or a larger value on dashboards. */
  refetchInterval?: number | false;
  /** Force a fetch on mount even when the global staleTime would treat the cache as fresh. */
  refetchOnMount?: boolean | "always";
};

export const useProvisionJobLogs = (
  jobId: string | null,
  enabled: boolean,
  options?: ProvisionJobLogsOptions
) =>
  useQuery<ProvisionJobLog[]>({
    queryKey: LOGS_QUERY_KEY(jobId ?? "__idle__"),
    queryFn: () => fetchProvisionJobLogs(jobId ?? ""),
    enabled: Boolean(jobId) && enabled,
    refetchInterval: options?.refetchInterval ?? 5000,
    refetchOnMount: options?.refetchOnMount ?? false
  });

export const useUploadProvisionCsv = () =>
  useMutation({
    mutationFn: (payload: UploadProvisionCsvPayload) => uploadProvisionCsv(payload),
    onSuccess: (data) => {
      // Invalidate all job list variants (any scope)
      queryClient.invalidateQueries({ queryKey: ["provision", "jobs"] });
      if (data?.job?.id) {
        queryClient.invalidateQueries({ queryKey: LOGS_QUERY_KEY(data.job.id) });
      }
    }
  });

export const useMyManagedStores = (enabled: boolean) =>
  useQuery({
    queryKey: MANAGED_STORES_QUERY_KEY,
    queryFn: () => fetchMyManagedStores(),
    enabled,
    staleTime: 60_000
  });

export const useMargaritavilleRolesByStore = (storeId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: rolesByStoreQueryKey(storeId ?? "__none__"),
    queryFn: () => fetchMargaritavilleRolesByStore(storeId as string),
    enabled: Boolean(storeId) && enabled,
    staleTime: 300_000
  });

export type { CatalogSliceGroup } from "./managedStoresReference";

export interface MyManagedStoresReference {
  storesQuery: ReturnType<typeof useMyManagedStores>;
  rows: ManagedStoreRow[];
  groups: CatalogSliceGroup[];
  roleByStoreId: Map<string, RolesByCatalogStore>;
  rolesLoading: boolean;
  rolesError: boolean;
  hasStores: boolean;
  downloadGroupedSummaryCsv: () => void;
  downloadFlatStoresCsv: () => void;
  copyAllStoreIds: (mode: "comma" | "newline") => Promise<void>;
  copyGroupStoreIds: (group: CatalogSliceGroup, mode: "comma" | "newline") => Promise<void>;
}

/** Managed stores + Margaritaville roles, grouped by catalog slice for efficient UI and CSV exports. */
export function useMyManagedStoresReference(enabled: boolean): MyManagedStoresReference {
  const storesQuery = useMyManagedStores(enabled);
  const rows = storesQuery.data ?? [];

  const rolesQueries = useQueries({
    queries: rows.map((row) => ({
      queryKey: rolesByStoreQueryKey(row.store_id),
      queryFn: () => fetchMargaritavilleRolesByStore(row.store_id),
      enabled: enabled && rows.length > 0,
      staleTime: 300_000
    }))
  });

  const roleByStoreId = useMemo(() => {
    const m = new Map<string, RolesByCatalogStore>();
    rows.forEach((row, i) => {
      const data = rolesQueries[i]?.data;
      if (data) {
        m.set(row.store_id, data);
      }
    });
    return m;
  }, [rows, rolesQueries]);

  const groups = useMemo(() => groupManagedStoresByCatalogSlice(rows, roleByStoreId), [rows, roleByStoreId]);

  const rolesLoading =
    Boolean(enabled) && rows.length > 0 && rolesQueries.some((q) => q.isPending || q.isFetching);
  const rolesError = Boolean(enabled) && rows.length > 0 && rolesQueries.some((q) => q.isError);

  const downloadGroupedSummaryCsv = useCallback(() => {
    if (groups.length === 0) return;
    triggerCsvDownload(buildManagedStoresGroupedSummaryCsv(groups), "my-locations-by-slice.csv");
  }, [groups]);

  const downloadFlatStoresCsv = useCallback(() => {
    if (rows.length === 0) return;
    triggerCsvDownload(buildManagedStoresFlatListCsv(rows, roleByStoreId), "my-store-ids-detail.csv");
  }, [rows, roleByStoreId]);

  const copyAllStoreIds = useCallback(
    async (mode: "comma" | "newline") => {
      if (rows.length === 0) return;
      const text = rows.map((r) => r.store_id).join(mode === "comma" ? ", " : "\n");
      await navigator.clipboard.writeText(text);
    },
    [rows]
  );

  const copyGroupStoreIds = useCallback(
    async (group: CatalogSliceGroup, mode: "comma" | "newline") => {
      if (group.storeIds.length === 0) return;
      const text = group.storeIds.join(mode === "comma" ? ", " : "\n");
      await navigator.clipboard.writeText(text);
    },
    []
  );

  return {
    storesQuery,
    rows,
    groups,
    roleByStoreId,
    rolesLoading,
    rolesError,
    hasStores: rows.length > 0,
    downloadGroupedSummaryCsv,
    downloadFlatStoresCsv,
    copyAllStoreIds,
    copyGroupStoreIds
  };
}
